import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Moon, Sun, Minus, Plus } from "lucide-react";
import { Button, Input } from "@tabipla/ui";

interface TripDatePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

export function TripDatePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: TripDatePickerProps) {
  const { t } = useTranslation();

  // Calculate initial nights from start/end dates
  const getInitialNights = (): number => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const [nights, setNights] = useState(getInitialNights);

  // Sync nights when dates change externally
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setNights(Math.max(0, diffDays));
    }
  }, [startDate, endDate]);

  // Update end date when nights change
  const updateEndDate = (newNights: number) => {
    if (!startDate) return;
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + newNights);
    onEndDateChange(end.toISOString().split("T")[0]);
  };

  const handleNightsChange = (newNights: number) => {
    const clampedNights = Math.max(0, Math.min(30, newNights)); // Max 30 nights
    setNights(clampedNights);
    updateEndDate(clampedNights);
  };

  const handleStartDateChange = (newStartDate: string) => {
    onStartDateChange(newStartDate);
    // Recalculate end date based on current nights
    if (newStartDate && nights >= 0) {
      const start = new Date(newStartDate);
      const end = new Date(start);
      end.setDate(end.getDate() + nights);
      onEndDateChange(end.toISOString().split("T")[0]);
    }
  };

  // Format date for display
  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
      weekday: "short",
    });
  };

  return (
    <div className="space-y-4">
      {/* Start Date */}
      <div className="space-y-2">
        <label className="flex items-center gap-1.5 text-sm font-medium">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {t("trips.startDate")}
          <span className="text-xs text-muted-foreground">({t("common.optional", "任意")})</span>
        </label>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
        />
      </div>

      {/* Nights Selector */}
      {startDate && (
        <div className="space-y-2">
          <label className="flex items-center gap-1.5 text-sm font-medium">
            <Moon className="h-4 w-4 text-muted-foreground" />
            {t("trips.duration", "旅行期間")}
          </label>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-lg border">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleNightsChange(nights - 1)}
                disabled={nights <= 0}
                className="h-10 w-10 rounded-r-none"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="flex min-w-[100px] items-center justify-center gap-1 px-3">
                <span className="text-lg font-bold">{nights}</span>
                <span className="text-sm text-muted-foreground">{t("trips.nights", "泊")}</span>
                <span className="text-lg font-bold">{nights + 1}</span>
                <span className="text-sm text-muted-foreground">{t("trips.days", "日")}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleNightsChange(nights + 1)}
                disabled={nights >= 30}
                className="h-10 w-10 rounded-l-none"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Date Summary */}
      {startDate && endDate && (
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
          <Sun className="h-4 w-4 text-amber-500" />
          <span className="font-medium">{formatDisplayDate(startDate)}</span>
          <span className="text-muted-foreground">→</span>
          <span className="font-medium">{formatDisplayDate(endDate)}</span>
        </div>
      )}
    </div>
  );
}
